import { Injectable, Injector, NgZone, OnDestroy } from '@angular/core';
import { SwUpdate } from '@angular/service-worker';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { APP_RELEASE } from '../app-release.generated';
import { FrontendDiagnosticsService } from './frontend-diagnostics.service';
import { OfflineDataService } from './offline-data.service';
import { PatientSessionService } from '../../staff/home/patient-session.service';

interface SparkRelease {
  version: string;
  buildId: string;
  builtAt: string;
}

@Injectable({ providedIn: 'root' })
export class AppVersionService implements OnDestroy {
  private latestRelease: SparkRelease | null = null;
  private isInitialized = false;
  private workerReadyRelease: string | null = null;
  private checkPromise: Promise<boolean> | null = null;
  private updateAvailableSubject = new BehaviorSubject<boolean>(false);
  private subscriptions = new Subscription();
  readonly promptRequests = new Subject<void>();
  status = 'Update not checked yet';
  isApplyingUpdate = false;
  recoveryRequired = false;

  private readonly onResume = () => {
    if (document.visibilityState !== 'hidden' && navigator.onLine) {
      this.zone.run(() => { void this.checkForUpdate(); });
    }
  };

  private diagnosticsInstance: FrontendDiagnosticsService | null = null;

  // FrontendDiagnosticsService injects this service to report the app version, so injecting it here as well is a
  // circular dependency (NG0200) that stops Spark from starting. Look it up when it is first needed instead.
  private get diagnostics(): FrontendDiagnosticsService {
    if (!this.diagnosticsInstance) {
      this.diagnosticsInstance = this.injector.get(FrontendDiagnosticsService);
    }
    return this.diagnosticsInstance;
  }

  constructor(
    private swUpdate: SwUpdate,
    private injector: Injector,
    private zone: NgZone
  ) {}

  initialize(): void {
    if (this.isInitialized) return;
    this.isInitialized = true;
    if (this.swUpdate.isEnabled) {
      this.subscriptions.add(this.swUpdate.versionUpdates.subscribe(evt => {
        if (evt.type === 'VERSION_READY') {
          const release = evt.latestVersion.appData as SparkRelease | undefined;
          this.workerReadyRelease = release ? release.version + '|' + release.buildId : null;
        }
        if (evt.type === 'VERSION_INSTALLATION_FAILED') {
          this.workerReadyRelease = null;
          this.diagnostics.addError('Spark update download failed', evt.error);
        }
      }));
      this.subscriptions.add(this.swUpdate.unrecoverable.subscribe(evt => {
        this.workerReadyRelease = null;
        this.recoveryRequired = true;
        this.status = 'Spark needs repair';
        this.updateAvailableSubject.next(true);
        this.diagnostics.addError('Spark service worker needs recovery', evt.reason);
        void this.checkForUpdate();
      }));
    }
    // A fallback navigation must not leave bypass parameters in shared links.
    const url = new URL(window.location.href);
    if (url.searchParams.has('spark-update')) {
      url.searchParams.delete('spark-update');
      url.searchParams.delete('ngsw-bypass');
      history.replaceState(history.state, '', url.toString());
    }
    window.addEventListener('online', this.onResume);
    window.addEventListener('pageshow', this.onResume);
    document.addEventListener('visibilitychange', this.onResume);
    void this.checkForUpdate();
  }

  getVersionRaw(): string {
    return APP_RELEASE.version;
  }

  getLatestVersionRaw(): string | null {
    return this.latestRelease?.version ?? null;
  }

  getDiagnosticVersionRaw(): string {
    return APP_RELEASE.version + ' (' + APP_RELEASE.buildId + ')';
  }

  getLatestDiagnosticVersionRaw(): string | null {
    return this.latestRelease ? this.latestRelease.version + ' (' + this.latestRelease.buildId + ')' : null;
  }

  updateAvailable$(): Observable<boolean> {
    return this.updateAvailableSubject.asObservable();
  }

  requestUpdatePrompt(): void {
    this.promptRequests.next();
  }

  checkForUpdate(): Promise<boolean> {
    if (this.checkPromise) return this.checkPromise;
    this.status = 'Checking for updates…';
    this.checkPromise = (async () => {
      try {
        const release = await this.fetchRelease();
        this.latestRelease = release;
        const available = this.recoveryRequired || release.buildId !== APP_RELEASE.buildId || release.version !== APP_RELEASE.version;
        this.status = this.recoveryRequired ? 'Spark needs repair' : available ? 'Update available' : 'Running latest version';
        this.updateAvailableSubject.next(available);
        if (available) this.diagnostics.addDiagnostic('Spark update available', this.getLatestDiagnosticVersionRaw()!);
        return available;
      } catch (error) {
        this.status = this.recoveryRequired ? 'Spark needs repair. Reconnect and try again.' : 'Update check unavailable. Try again when online.';
        this.diagnostics.addError('Spark update check failed', String(error));
        return false;
      } finally {
        this.checkPromise = null;
      }
    })();
    return this.checkPromise;
  }

  private async fetchRelease(): Promise<SparkRelease> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL('spark-version.json', document.baseURI);
      url.searchParams.set('ngsw-bypass', 'true');
      url.searchParams.set('check', String(Date.now()));
      const response = await fetch(url.toString(), { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('Release metadata returned HTTP ' + response.status);
      const release = await response.json();
      if (!release || typeof release.version !== 'string' || !/^[0-9]+\.[0-9]+\.[0-9]+$/.test(release.version)
        || typeof release.buildId !== 'string' || !/^[a-zA-Z0-9._-]{1,100}$/.test(release.buildId)
        || typeof release.builtAt !== 'string' || !Number.isFinite(Date.parse(release.builtAt))) {
        throw new Error('The published Spark release metadata is invalid.');
      }
      return release;
    } finally {
      clearTimeout(timer);
    }
  }

  async applyUpdate(): Promise<boolean> {
    if (this.isApplyingUpdate) return false;
    this.isApplyingUpdate = true;
    try {
      const sessions = this.injector.get(PatientSessionService);
      const offline = this.injector.get(OfflineDataService);
      if (sessions.hasRunningTimers()) throw new Error('Finish or stop running timers before updating Spark.');
      const initiallySafe = await offline.prepareForAppUpdate();
      if (!initiallySafe || sessions.hasRunningTimers()) {
        throw new Error('Spark is still saving or syncing. Wait for local saves to finish before updating. If saving fails, use Send logs and contact support.');
      }
      // Recheck the deployed release instead of trusting old metadata or a worker hash.
      const release = await this.fetchRelease();
      if (!this.recoveryRequired && release.buildId === APP_RELEASE.buildId && release.version === APP_RELEASE.version) {
        this.latestRelease = release;
        this.updateAvailableSubject.next(false);
        this.status = 'Running latest version';
        return false;
      }
      if (this.swUpdate.isEnabled && this.workerReadyRelease !== release.version + '|' + release.buildId) {
        let timeout: ReturnType<typeof setTimeout> | undefined;
        try {
          await Promise.race([
            this.swUpdate.checkForUpdate(),
            new Promise<never>((_, reject) => { timeout = setTimeout(() => reject(new Error('Update download timed out')), 15000); })
          ]);
        } catch (error) {
          this.diagnostics.addError('Spark worker update unavailable; trying network recovery', String(error));
        } finally {
          if (timeout) clearTimeout(timeout);
        }
      }
      // A timer may have completed or a save may have started while checking/downloading.
      const safeToReload = await offline.prepareForAppUpdate();
      if (!safeToReload || sessions.hasRunningTimers()) {
        throw new Error('Spark is still saving or syncing. Wait for local saves to finish, then try Update App again.');
      }
      this.diagnostics.addDiagnostic('Spark update accepted', release.version + ' (' + release.buildId + ')');
      if (!this.recoveryRequired && this.workerReadyRelease === release.version + '|' + release.buildId) {
        // Reload rather than activating new lazy chunks in an old application shell.
        if (!offline.canReloadForAppUpdate() || sessions.hasRunningTimers()) throw new Error('Spark is still saving or syncing. Please try again.');
        this.reloadApp();
      } else {
        // A failed/absent worker must not prevent a user-confirmed online update.
        // Keep the registration and offline caches intact, including when recovery is deferred.
        // Verify an internally consistent deployment before abandoning a cached working shell.
        let verificationError: Error | null = null;
        for (let attempt = 0; attempt < 3; attempt++) {
          try {
            const manifestBytes = await this.fetchUpdateFile('ngsw.json');
            const manifest = JSON.parse(new TextDecoder().decode(manifestBytes));
            if (manifest.appData?.version !== release.version || manifest.appData?.buildId !== release.buildId
              || typeof manifest.index !== 'string' || !manifest.hashTable) {
              throw new Error('The new release is still being published. Please try updating again in a moment.');
            }
            const files = Object.keys(manifest.hashTable).filter(file =>
              file === manifest.index || /^\/[^/]+\.(js|css)$/.test(file));
            if (!files.includes(manifest.index) || !files.some(file => file.endsWith('.js'))) {
              throw new Error('The new release is incomplete. Please try updating again in a moment.');
            }
            await Promise.all(files.map(file => this.fetchUpdateFile(file,
              file === manifest.index ? 'text/html' : file.endsWith('.js') ? 'javascript' : 'text/css')));
            verificationError = null;
            break;
          } catch (error) {
            verificationError = error instanceof Error ? error : new Error(String(error));
            if (attempt < 2) await new Promise(resolve => setTimeout(resolve, 2000));
          }
        }
        if (verificationError) throw verificationError;
        const safeAfterRecovery = await offline.prepareForAppUpdate();
        if (!safeAfterRecovery || !offline.canReloadForAppUpdate() || sessions.hasRunningTimers()) {
          throw new Error('Spark is still saving or syncing. Wait for local saves to finish before updating.');
        }
        const target = new URL(window.location.href);
        target.searchParams.set('ngsw-bypass', 'true');
        target.searchParams.set('spark-update', release.buildId);
        this.reloadApp(target.toString());
      }
      return true;
    } catch (error) {
      this.diagnostics.addError('Spark update deferred or failed', String(error));
      throw error;
    } finally {
      this.isApplyingUpdate = false;
    }
  }

  private reloadApp(url?: string): void {
    if (url) window.location.assign(url);
    else window.location.reload();
  }

  private async fetchUpdateFile(file: string, expectedContentType?: string): Promise<ArrayBuffer> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    try {
      const url = new URL(file, document.baseURI);
      if (url.origin !== window.location.origin) throw new Error('Invalid update file origin.');
      url.searchParams.set('ngsw-bypass', 'true');
      const response = await fetch(url.toString(), { cache: 'no-store', signal: controller.signal });
      if (!response.ok) throw new Error('An update file is unavailable. Please try again in a moment.');
      if (expectedContentType && !response.headers.get('content-type')?.toLowerCase().includes(expectedContentType)) {
        throw new Error('An update file is unavailable. Please try again in a moment.');
      }
      return await response.arrayBuffer();
    } finally {
      clearTimeout(timer);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    window.removeEventListener('online', this.onResume);
    window.removeEventListener('pageshow', this.onResume);
    document.removeEventListener('visibilitychange', this.onResume);
  }
}
